import { useState, useEffect } from 'react';

// Type pour les entrées du cache
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// Type pour les réponses d'API
interface ApiResponse<T> {
  data: T | null;
  error: any;
}

// Cache global partagé entre les instances du hook
export const globalCache: Record<string, CacheEntry<any>> = {};

// Durée de validité du cache par défaut (5 minutes)
const DEFAULT_CACHE_DURATION = 5 * 60 * 1000;

/**
 * Hook personnalisé pour mettre en cache des données avec expiration
 * @param key Clé unique pour identifier les données dans le cache
 * @param fetchFn Fonction asynchrone pour récupérer les données
 * @param cacheDuration Durée de validité du cache en millisecondes (par défaut: 5 minutes)
 * @param dependencies Tableau de dépendances qui déclenchent un rechargement des données
 */
export function useCachedData<T>(
  key: string,
  fetchFn: () => Promise<T | ApiResponse<T>>,
  cacheDuration: number = DEFAULT_CACHE_DURATION,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // Fonction pour vérifier si les données en cache sont valides
  const isCacheValid = (cacheKey: string): boolean => {
    const entry = globalCache[cacheKey];
    if (!entry) return false;
    
    // Ne jamais mettre en cache uniquement les recherches et les paramètres d'application
    if (cacheKey.includes('app_settings') || 
        cacheKey.includes('relation_unlock_price') || 
        cacheKey.includes('relation_deletion_price') ||
        cacheKey.includes('search') ||
        cacheKey.includes('face-search') ||
        cacheKey.includes('detect')) {
      return false;
    }
    
    const now = Date.now();
    
    // Cache plus long pour les images de profil (2 heures)
    if (cacheKey.includes('avatar_url') || cacheKey.includes('profiles/')) {
      return entry.expiresAt > now + (2 * 60 * 60 * 1000);
    }
    
    // Cache de 24h pour les autres images
    if (cacheKey.includes('/storage/v1/object/public/')) {
      return entry.expiresAt > now + (24 * 60 * 60 * 1000);
    }
    
    return entry.expiresAt > now;
  };

  // Fonction pour charger les données
  const loadData = async (forceRefresh: boolean = false) => {
    if (!forceRefresh && isCacheValid(key)) {
      const cachedData = globalCache[key].data;
      setData(cachedData);
      setIsLoading(false);
      setLastUpdated(globalCache[key].timestamp);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchFn();
      
      if (response && typeof response === 'object' && 'data' in response && 'error' in response) {
        const apiResponse = response as ApiResponse<T>;
        
        if (apiResponse.error) {
          throw apiResponse.error;
        }
        
        if (apiResponse.data) {
          const now = Date.now();
          globalCache[key] = {
            data: apiResponse.data,
            timestamp: now,
            expiresAt: now + cacheDuration
          };
          
          setData(apiResponse.data);
          setLastUpdated(now);
          setError(null);
        }
      } else {
        const responseData = response as T;
        const now = Date.now();
        
        globalCache[key] = {
          data: responseData,
          timestamp: now,
          expiresAt: now + cacheDuration
        };
        
        setData(responseData);
        setLastUpdated(now);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      console.error(`Error loading data for key "${key}":`, err);
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les données au montage et lorsque les dépendances changent
  useEffect(() => {
    loadData();
  }, dependencies);

  return {
    data,
    isLoading,
    error,
    lastUpdated,
    refresh: () => loadData(true),
    invalidateCache: async () => {
      console.log(`Invalidation du cache pour la clé: ${key}`);
      
      delete globalCache[key];
      
      // Invalidation plus ciblée pour les profils
      if (key.includes('profile')) {
        // Ne supprimer que les caches directement liés au profil modifié
        const profileId = key.split('/').pop();
        if (profileId) {
          Object.keys(globalCache).forEach(cacheKey => {
            if (cacheKey.includes(`profile/${profileId}`)) {
              console.log(`Invalidation du cache associé: ${cacheKey}`);
              delete globalCache[cacheKey];
            }
          });
        }
      } else if (key.includes('relations') || key.includes('delete_relation')) {
        // Pour les relations et les suppressions, invalider tous les caches liés
        Object.keys(globalCache).forEach(cacheKey => {
          if (cacheKey.includes('relations') || 
              cacheKey.includes('creator') || 
              cacheKey.includes('certifier') ||
              cacheKey.includes('user-relations')) {
            console.log(`Invalidation du cache associé: ${cacheKey}`);
            delete globalCache[cacheKey];
          }
        });
        
        // Pour la suppression, nettoyer aussi le localStorage des relations
        if (key.includes('delete_relation')) {
          try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const storageKey = localStorage.key(i);
              if (storageKey && storageKey.includes('relation')) {
                keysToRemove.push(storageKey);
              }
            }
            
            keysToRemove.forEach(storageKey => {
              console.log(`Suppression de la clé localStorage: ${storageKey}`);
              localStorage.removeItem(storageKey);
            });
          } catch (e) {
            console.error("Erreur lors du nettoyage du localStorage:", e);
          }
        }
      }
      
      await loadData(true);
    }
  };
} 